import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Check, Gift, Users, BookOpen, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = "ASSTj5A6vfhyeolcJJaZq-Um79owztjiz3G40wlByGr1Hj84RNyqm6UT6TyFH-uGPaPfJliy9OwJbnF8";

const AMOUNTS = [
  { id: "250", label: "$250", value: 250 },
  { id: "500", label: "$500", value: 500 },
  { id: "1000", label: "$1,000", value: 1000 },
  { id: "2500", label: "$2,500", value: 2500 },
  { id: "5000", label: "$5,000", value: 5000 },
];

function DonateSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId || attempts >= 5) {
      setStatus(attempts >= 5 ? "timeout" : "error");
      return;
    }
    try {
      const res = await axios.get(`${API}/donate/stripe/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    } catch {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="donate-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto text-center p-10 rounded-2xl glass-light"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-serif font-bold text-white mb-2">Processing Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your donation.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-white mb-2" data-testid="success-message">Thank You!</h2>
            <p className="text-muted-foreground mb-6">Your donation has been received. Thank you for supporting VR education!</p>
            <a href="/donate" className="btn-primary inline-block" data-testid="back-to-donate">Back to Donate</a>
          </>
        )}
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <Heart className="w-16 h-16 text-cta mx-auto mb-6" />
            <h2 className="text-2xl font-serif font-bold text-white mb-2">Payment Status Unknown</h2>
            <p className="text-muted-foreground mb-6">We couldn't confirm your payment. If you were charged, please contact us.</p>
            <a href="/donate" className="btn-primary inline-block">Try Again</a>
          </>
        )}
      </motion.div>
    </div>
  );
}

function PayPalButton({ amount, packageId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  useEffect(() => {
    if (document.querySelector('#paypal-sdk')) {
      setSdkReady(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
    script.onload = () => setSdkReady(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !amount) return;

    const container = document.getElementById('paypal-button-container');
    if (container) container.innerHTML = '';

    window.paypal.Buttons({
      createOrder: async () => {
        setLoading(true);
        try {
          const body = packageId ? { package_id: packageId } : { custom_amount: amount };
          const res = await axios.post(`${API}/donate/paypal/create`, body);
          return res.data.order_id;
        } catch (err) {
          setLoading(false);
          throw err;
        }
      },
      onApprove: async (data) => {
        try {
          const res = await axios.post(`${API}/donate/paypal/capture/${data.orderID}`);
          if (res.data.payment_status === "paid") {
            onSuccess();
          }
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => setLoading(false),
      onError: () => setLoading(false),
      style: {
        layout: 'horizontal',
        color: 'gold',
        shape: 'rect',
        label: 'donate',
        height: 45,
      }
    }).render('#paypal-button-container');
  }, [sdkReady, amount, packageId, onSuccess]);

  return (
    <div data-testid="paypal-button">
      {loading && (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">Processing...</span>
        </div>
      )}
      <div id="paypal-button-container" className={loading ? 'opacity-50 pointer-events-none' : ''} />
    </div>
  );
}

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const [selectedAmount, setSelectedAmount] = useState("250");
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [paypalSuccess, setPaypalSuccess] = useState(false);

  if (searchParams.get("session_id")) {
    return <DonateSuccess />;
  }

  if (paypalSuccess) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="paypal-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center p-10 rounded-2xl glass-light"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-white mb-2">Thank You!</h2>
          <p className="text-muted-foreground mb-6">Your PayPal donation has been received. Thank you for supporting VR education!</p>
          <a href="/donate" className="btn-primary inline-block" onClick={() => setPaypalSuccess(false)}>Make Another Donation</a>
        </motion.div>
      </div>
    );
  }

  const currentAmount = isCustom ? parseFloat(customAmount) || 0 : AMOUNTS.find(a => a.id === selectedAmount)?.value || 0;
  const currentPackageId = isCustom ? null : selectedAmount;

  const impacts = [
    { icon: Users, title: "Support Students", description: "Help provide VR equipment and access to underserved students and schools." },
    { icon: BookOpen, title: "Develop Content", description: "Fund the creation of new immersive educational experiences and virtual environments." },
    { icon: Gift, title: "Expand Programs", description: "Enable us to reach more communities and develop specialized programs for diverse learners." },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="donate-page">
      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-cta/20 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-cta" />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="donate-title">
              Support Our Mission
            </h1>
            <p className="text-xl text-muted-foreground">
              Your donation helps us provide transformative VR education experiences to learners worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Checkout */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl glass-light p-8">
              <h2 className="text-2xl font-serif font-bold text-white mb-6 text-center">Choose Your Donation Amount</h2>

              {/* Amount Selection */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="amount-selector">
                {AMOUNTS.map((amt) => (
                  <button
                    key={amt.id}
                    onClick={() => { setSelectedAmount(amt.id); setIsCustom(false); }}
                    className={`py-4 px-3 rounded-xl text-lg font-semibold transition-all ${
                      !isCustom && selectedAmount === amt.id
                        ? 'bg-cta text-background shadow-lg shadow-cta/30'
                        : 'bg-card border border-border text-white hover:border-primary/50'
                    }`}
                    data-testid={`amount-${amt.id}`}
                  >
                    {amt.label}
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <button
                  onClick={() => setIsCustom(true)}
                  className={`w-full py-3 px-4 rounded-xl text-sm transition-all mb-2 ${
                    isCustom ? 'bg-cta/20 border-2 border-cta text-cta' : 'bg-card border border-border text-muted-foreground hover:border-primary/50'
                  }`}
                  data-testid="custom-amount-toggle"
                >
                  Custom Amount
                </button>
                {isCustom && (
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-lg font-bold">$</span>
                    <input
                      type="number"
                      min="1"
                      max="10000"
                      step="1"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full py-3 pl-10 pr-4 rounded-xl bg-card border border-border text-white text-lg focus:border-primary focus:outline-none"
                      data-testid="custom-amount-input"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Current Amount Display */}
              {currentAmount > 0 && (
                <div className="text-center mb-6">
                  <p className="text-muted-foreground text-sm">Your donation</p>
                  <p className="text-4xl font-bold text-cta" data-testid="display-amount">${currentAmount.toFixed(2)}</p>
                </div>
              )}

              {/* Payment via PayPal */}
              <div className="space-y-4">
                {currentAmount >= 1 ? (
                  <PayPalButton
                    amount={currentAmount}
                    packageId={currentPackageId}
                    onSuccess={() => setPaypalSuccess(true)}
                  />
                ) : (
                  <div className="text-center py-3 text-sm text-muted-foreground">
                    Select an amount to enable PayPal
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12">
            Your Impact
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impacts.map((impact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl glass-light text-center card-hover"
                data-testid={`impact-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-cta/20 flex items-center justify-center mx-auto mb-4">
                  <impact.icon className="w-7 h-7 text-cta" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{impact.title}</h3>
                <p className="text-sm text-muted-foreground">{impact.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Thank You */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center p-8 rounded-2xl glass-light">
            <Heart className="w-12 h-12 text-cta mx-auto mb-6" />
            <h2 className="text-2xl font-serif font-bold text-white mb-4">Thank You for Your Support</h2>
            <p className="text-muted-foreground">
              Every contribution, no matter the size, helps us bring transformative VR education to more students and communities around the world.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
